import { useCallback, useEffect, useState } from "react";
import { FaArrowsSpin, FaWeightHanging } from "react-icons/fa6";
import { FiAlertTriangle } from "react-icons/fi";
import { GiChicken, GiGoat, GiNewBorn, GiPig, GiSheep } from "react-icons/gi";
import { GoClockFill } from "react-icons/go";
import { IoCalendarOutline } from "react-icons/io5";
import { LuCopyPlus } from "react-icons/lu";
import { PiCowDuotone } from "react-icons/pi";
import { useNavigate } from "react-router-dom";
import AjudaTela from "../../components/AjudaTela";
import Layout from "../../components/Layout";
import ModalCadastroAnimal from "../../components/modais/ModalCadastroAnimal";
import { api } from "../../services/api";
import { repositorioAnimal, repositorioNatalidade } from "../../sync/repositories/pecuaria";

const AJUDA = {
  titulo: "Registro de Natalidade",
  descricao:
    "Registre aqui o nascimento de um filhote. Ao salvar, o sistema cria automaticamente um novo Animal (o filhote) já vinculado à mãe escolhida — você não precisa cadastrar o filhote separadamente em Animais.",
  secoes: [
    {
      titulo: "Dados do Nascimento",
      campos: [
        {
          nome: "Mãe",
          obrigatorio: true,
          explicacao: "Só aparecem fêmeas marcadas como aptas para reprodução. Clique no ícone de '?' ao lado do campo para ver tempo de gestação e idade mínima por espécie.",
        },
        {
          nome: "Número do Brinco",
          obrigatorio: true,
          explicacao: "Identificação do filhote recém-nascido — vira o brinco do novo Animal criado.",
        },
        {
          nome: "SISBOV do Filhote",
          obrigatorio: false,
          explicacao: "Identificação oficial do filhote, se já disponível no momento do registro.",
        },
        {
          nome: "Data de Nascimento",
          obrigatorio: true,
          explicacao: "Data em que o parto ocorreu.",
        },
        {
          nome: "Sexo do Filhote",
          obrigatorio: true,
          explicacao: "Define o sexo do novo Animal criado a partir deste registro.",
        },
        {
          nome: "Peso ao Nascer",
          obrigatorio: false,
          explicacao: "Peso do filhote no momento do nascimento, em kg — útil para acompanhar o desenvolvimento depois em Pesagens.",
        },
      ],
    },
  ],
  dicas: [
    "Cadastrar, editar ou excluir um registro de nascimento exige conexão com a internet — apenas a consulta funciona offline.",
    "Excluir um registro de nascimento não remove automaticamente o Animal filhote criado — se for o caso, remova-o manualmente no cadastro de Animais.",
  ],
};

export default function GestaoNatalidade() {
  const navigate = useNavigate();
  const [loading, setLoading] = useState(false);
  const [carregandoLista, setCarregandoLista] = useState(true);
  const [nascimentos, setNascimentos] = useState([]);
  const [nascimentosFiltrados, setNascimentosFiltrados] = useState([]);
  const [editando, setEditando] = useState(false);
  const [editandoId, setEditandoId] = useState(null);
  const [mostrarFormulario, setMostrarFormulario] = useState(false);
  const [mostrarPopupMae, setMostrarPopupMae] = useState(false);
  
  // Dados para os selects
  const [animais, setAnimais] = useState([]);
  const [mostrarModalAnimal, setMostrarModalAnimal] = useState(false);
  
  const [filtros, setFiltros] = useState({
    busca: "",
    data_inicio: "",
    data_fim: "",
    sexo: ""
  });
  
  const [formData, setFormData] = useState({
    mae: "",
    data_nascimento: "",
    peso_nascimento: "",
    sisbov: "",
    brinco: "",
    sexo: "",
    observacoes: "",
  });

  const dadosGestacao = {
    BOVINO: {
      nome: "Bovino",
      tempoGestacao: "~9 meses",
      pesoMinimo: "300 kg",
      idadeMinima: "22 meses",
      intervaloPartos: "12-14 meses",
      icone: <PiCowDuotone size={20} />
    },
    SUINO: {
      nome: "Suíno",
      tempoGestacao: "3 meses, 3 semanas e 3 dias",
      pesoMinimo: "120 kg",
      idadeMinima: "8 meses",
      intervaloPartos: "5-6 meses",
      icone: <GiPig size={20} />
    },
    CAPRINO: {
      nome: "Caprino",
      tempoGestacao: "5 meses",
      pesoMinimo: "35 kg",
      idadeMinima: "7 meses",
      intervaloPartos: "8-10 meses",
      icone: <GiGoat size={20} />
    },
    OVINO: {
      nome: "Ovino",
      tempoGestacao: "5 meses",
      pesoMinimo: "40 kg",
      idadeMinima: "7 meses",
      intervaloPartos: "8-10 meses",
      icone: <GiSheep size={20}/>
    },
    AVES: {
      nome: "Aves",
      tempoGestacao: "21 dias (ovos)",
      pesoMinimo: "1.5 kg",
      idadeMinima: "5 meses",
      intervaloPartos: "Postura diária",
      icone: <GiChicken size={20} />
    }
  };

  const carregarAnimais = useCallback(async () => {
    try {
      const response = await repositorioAnimal.listar();
      const dados = response.data.results || response.data;
      const animaisArray = Array.isArray(dados) ? dados : [];
      const femeasReprodutoras = animaisArray.filter(a => a.sexo === "F" && a.pode_reproduzir === true);
      setAnimais(femeasReprodutoras);
    } catch (error) {
      console.error("Erro ao carregar animais:", error);
      setAnimais([]);
    }
  }, []);

  // Carregar nascimentos
  const carregarNascimentos = useCallback(async () => {
    try {
      setCarregandoLista(true);
      const response = await repositorioNatalidade.listar();
      const dados = response.data.results || response.data;
      setNascimentos(Array.isArray(dados) ? dados : []);
      setNascimentosFiltrados(Array.isArray(dados) ? dados : []);
    } catch (error) {
      console.error("Erro ao carregar nascimentos:", error);
      alert("Erro ao carregar lista de nascimentos");
    } finally {
      setCarregandoLista(false);
    }
  }, []);

  useEffect(() => {
    carregarNascimentos();
    carregarAnimais();
  }, [carregarNascimentos, carregarAnimais]);

  const handleChange = (e) => {
    const { name, value } = e.target;
    setFormData(prev => ({ ...prev, [name]: value }));
  };

  const validarFormulario = () => {
    if (!formData.mae) {
      alert("Selecione a mãe.");
      return false;
    }
    if (!formData.data_nascimento) {
      alert("Informe a data de nascimento.");
      return false;
    }
    if (!formData.brinco.trim()) {
      alert("Informe o número do brinco do filhote.");
      return false;
    }
    if (!formData.sexo) {
      alert("Selecione o sexo do filhote.");
      return false;
    }
    return true;
  };

  const handleSubmit = async (e) => {
    e.preventDefault();
    
    if (!validarFormulario()) return;
    
    setLoading(true);

    const payload = {
      mae: formData.mae,
      data_nascimento: formData.data_nascimento,
      peso_nascimento: formData.peso_nascimento ? Number(formData.peso_nascimento) : null,
      sisbov: formData.sisbov || "",
      brinco: formData.brinco,
      sexo: formData.sexo,
      observacoes: formData.observacoes || "",
    };

    try {
      if (editando) {
        await api.put(`/pecuaria/natalidade/${editandoId}/`, payload);
        alert("Nascimento atualizado com sucesso!");
        handleCancel();
        carregarNascimentos();
        carregarAnimais();
      } else {
        await api.post("/pecuaria/natalidade/", payload);
        alert("Nascimento registrado com sucesso!");
        handleCancel();
        carregarNascimentos();
        carregarAnimais();
      }
    } catch (error) {
      console.error("Erro:", error);
      const data = error.response?.data;
      if (data && typeof data === 'object' && !Array.isArray(data)) {
        const erros = Object.values(data).flat();
        alert(erros.join("\n"));
      } else if (Array.isArray(data)) {
        alert(data.join("\n"));
      } else {
        alert("Erro ao salvar nascimento. Verifique os dados e tente novamente.");
      }
    } finally {
      setLoading(false);
    }
  };

  const handleEditar = (nascimento) => {
    setFormData({
      mae: nascimento.mae?.id || nascimento.mae || "",
      data_nascimento: nascimento.data_nascimento || "",
      peso_nascimento: nascimento.peso_nascimento || "",
      sisbov: nascimento.sisbov || "",
      brinco: nascimento.brinco || "",
      sexo: nascimento.sexo || "",
      observacoes: nascimento.observacoes || "",
    });
    setEditando(true);
    setEditandoId(nascimento.id);
    setMostrarFormulario(true);
    
    setTimeout(() => {
      window.scrollTo({ top: 0, behavior: 'smooth' });
    }, 100);
  };

  const handleNovoNascimento = () => {
    handleCancel();
    setMostrarFormulario(true);
  };

  const handleCancel = () => {
    setFormData({
      mae: "",
      data_nascimento: "",
      peso_nascimento: "",
      sisbov: "",
      brinco: "",
      sexo: "",
      observacoes: "",
    });
    setEditando(false);
    setEditandoId(null);
    setMostrarFormulario(false);
  };

  const handleExcluir = async (id) => {
    if (!window.confirm("Tem certeza que deseja excluir este registro de nascimento? Esta ação não pode ser desfeita.")) {
      return;
    }
    
    try {
      await api.delete(`/pecuaria/natalidade/${id}/`);
      alert("Registro de nascimento excluído com sucesso!");
      carregarNascimentos();
      carregarAnimais();
      
      if (editandoId === id) {
        handleCancel();
      }
    } catch (error) {
      console.error("Erro ao excluir:", error);
      alert("Erro ao excluir registro de nascimento.");
    }
  };

  // Aplicar filtros
  const aplicarFiltros = () => {
    let filtrados = Array.isArray(nascimentos) ? [...nascimentos] : [];
    
    if (filtros.busca) {
      const buscaLower = filtros.busca.toLowerCase();
      filtrados = filtrados.filter(n => 
        n.brinco?.toLowerCase().includes(buscaLower) ||
        n.sisbov?.toLowerCase().includes(buscaLower) ||
        n.mae_sisbov?.toLowerCase().includes(buscaLower)
      );
    }
    
    if (filtros.data_inicio) {
      filtrados = filtrados.filter(n => n.data_nascimento >= filtros.data_inicio);
    }
    
    if (filtros.data_fim) {
      filtrados = filtrados.filter(n => n.data_nascimento <= filtros.data_fim);
    }
    
    if (filtros.sexo) {
      filtrados = filtrados.filter(n => n.sexo === filtros.sexo);
    }
    
    setNascimentosFiltrados(filtrados);
  };

  const limparFiltros = () => {
    setFiltros({ busca: "", data_inicio: "", data_fim: "", sexo: "" });
    setNascimentosFiltrados(Array.isArray(nascimentos) ? nascimentos : []);
  };

  const formatarData = (data) => {
    if (!data) return "—";
    const [ano, mes, dia] = data.split('-');
    return `${dia}/${mes}/${ano}`;
  };

  const formatarPeso = (peso) => {
    if (!peso && peso !== 0) return "—";
    return `${Number(peso).toLocaleString('pt-BR', { minimumFractionDigits: 2, maximumFractionDigits: 2 })} kg`;
  };

  const getSexoLabel = (sexo) => {
    return sexo === "M" ? "Macho" : "Fêmea";
  };

  const getSexoColor = (sexo) => {
    return sexo === "M" ? "bg-blue-100 text-blue-800" : "bg-pink-100 text-pink-800";
  };

  const getAnimalDisplay = (animal) => {
  const identificacao = animal.sisbov || animal.numero_identificacao || `ID: ${animal.id}`;
  const nome = animal.nome || "Sem nome";
  
  const calcularIdadeDetalhada = (dataNascimento, idadeMeses, idadeObj) => {
    if (dataNascimento) {
      const nascimento = new Date(dataNascimento);
      const hoje = new Date();
      
      const diffTime = Math.abs(hoje - nascimento);
      const diffDias = Math.floor(diffTime / (1000 * 60 * 60 * 24));
      
      if (diffDias === 0) {
        return "Recém-nascido (hoje)";
      }
      
      if (diffDias < 30) {
        return `${diffDias} ${diffDias === 1 ? 'dia' : 'dias'}`;
      }
      
      // Calcular meses e anos
      let meses = (hoje.getFullYear() - nascimento.getFullYear()) * 12;
      meses -= nascimento.getMonth();
      meses += hoje.getMonth();
      
      if (hoje.getDate() < nascimento.getDate()) {
        meses -= 1;
      }
      
      if (meses < 12) {
        // Menos de 1 ano - mostrar em meses
        return `${meses} ${meses === 1 ? 'mês' : 'meses'}`;
      }
      
      // 1 ano ou mais
      const anos = Math.floor(meses / 12);
      const mesesRestantes = meses % 12;
      
      let texto = `${anos} ${anos === 1 ? 'ano' : 'anos'}`;
      if (mesesRestantes > 0) {
        texto += ` e ${mesesRestantes} ${mesesRestantes === 1 ? 'mês' : 'meses'}`;
      }
      return texto;
    }
    
    if (idadeMeses !== undefined && idadeMeses !== null) {
      if (idadeMeses < 1) {
        return "Menos de 1 mês";
      }
      if (idadeMeses < 12) {
        return `${idadeMeses} ${idadeMeses === 1 ? 'mês' : 'meses'}`;
      }
      const anos = Math.floor(idadeMeses / 12);
      const meses = idadeMeses % 12;
      let texto = `${anos} ${anos === 1 ? 'ano' : 'anos'}`;
      if (meses > 0) {
        texto += ` e ${meses} ${meses === 1 ? 'mês' : 'meses'}`;
      }
      return texto;
    }
    
    if (idadeObj) {
      const { anos, meses } = idadeObj;
      if (anos === 0 && meses === 0) return "Recém-nascido";
      if (anos === 0) return `${meses} ${meses === 1 ? 'mês' : 'meses'}`;
      let texto = `${anos} ${anos === 1 ? 'ano' : 'anos'}`;
      if (meses > 0) texto += ` e ${meses} ${meses === 1 ? 'mês' : 'meses'}`;
      return texto;
    }
    
    return "Idade não informada";
  };
  
  const idadeTexto = calcularIdadeDetalhada(
    animal.data_nascimento,
    animal.idade_meses,
    animal.idade
  );
  
  const especie = animal.especie_nome || "";
  const raca = animal.raca_nome || "";
  
  return `${identificacao} - ${nome} | ${idadeTexto} | ${especie} ${raca}`;
};

  const PopupInfoMae = () => (
    <div className="fixed inset-0 bg-black bg-opacity-50 flex items-center justify-center z-50 p-4" onClick={() => setMostrarPopupMae(false)}>
      <div className="bg-white rounded-lg shadow-xl w-full max-w-4xl" style={{ height: '80vh', maxHeight: '80vh' }} onClick={(e) => e.stopPropagation()}>
        <div className="bg-white border-b border-gray-200 p-4 rounded-t-lg flex justify-between items-center sticky top-0">
          <h3 className="text-xl font-bold text-gray-800 flex items-center gap-2">
            <GiNewBorn size={24} />
            Informações sobre Matrizes e Reprodução
          </h3>
          <button
            onClick={() => setMostrarPopupMae(false)}
            className="text-gray-500 hover:text-gray-700"
          >
            <svg className="w-6 h-6" fill="none" stroke="currentColor" viewBox="0 0 24 24">
              <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M6 18L18 6M6 6l12 12" />
            </svg>
          </button>
        </div>
        
        {/* Conteúdo rolável */}
        <div className="overflow-y-auto p-6" style={{ height: 'calc(80vh - 130px)' }}>
          {/* Informações Gerais */}
          <div className="mb-6 p-4 bg-blue-50 rounded-lg border border-blue-200">
            <h4 className="font-semibold text-blue-800 mb-2 flex items-center gap-2"><FiAlertTriangle size={20} /> Importante</h4>
            <p className="text-blue-700">
              Apenas <strong>fêmeas (matrizes)</strong> podem ser selecionadas como mãe. Cada espécie possui 
              diferentes tempos de gestação, idades mínimas para reprodução e cuidados específicos.
            </p>
          </div>

          {/* Tabela de Espécies */}
          <div className="space-y-4">
            {Object.entries(dadosGestacao).map(([key, especie]) => (
              <div key={key} className="border border-gray-200 rounded-lg overflow-hidden hover:shadow-md transition-shadow">
                <div className="bg-gray-50 p-4 border-b border-gray-200">
                  <h4 className="text-lg font-semibold text-gray-800 flex items-center gap-2">
                    <span className="text-2xl">{especie.icone}</span>
                    {especie.nome}
                  </h4>
                </div>
                <div className="p-4 grid grid-cols-1 md:grid-cols-2 gap-4">
                  <div>
                    <p className="flex items-start gap-2 mb-2">
                      <span className="font-medium text-gray-700 min-w-[120px] flex items-center gap-2"><GoClockFill size={20} /> Tempo de Gestação:</span>
                      <span className="text-green-600 font-semibold">{especie.tempoGestacao}</span>
                    </p>
                    <p className="flex items-start gap-2 mb-2">
                      <span className="font-medium text-gray-700 min-w-[120px] flex items-center gap-2"><FaWeightHanging size={20} /> Peso Mínimo:</span>
                      <span>{especie.pesoMinimo}</span>
                    </p>
                    <p className="flex items-start gap-2 mb-2">
                      <span className="font-medium text-gray-700 min-w-[120px] flex items-center gap-2"><IoCalendarOutline size={20} /> Idade Mínima:</span>
                      <span>{especie.idadeMinima}</span>
                    </p>
                    <p className="flex items-start gap-2 mb-2">
                      <span className="font-medium text-gray-700 min-w-[120px] flex items-center gap-2"><FaArrowsSpin size={20} /> Intervalo entre Partos:</span>
                      <span>{especie.intervaloPartos}</span>
                    </p>
                  </div>
                </div>
              </div>
            ))}
          </div>
        </div>
        
        {/* Footer fixo */}
        <div className="bg-gray-50 border-t border-gray-200 p-4 rounded-b-lg flex justify-end sticky bottom-0">
          <button
            onClick={() => setMostrarPopupMae(false)}
            className="bg-blue-600 hover:bg-blue-700 text-white px-4 py-2 rounded-lg transition-colors"
          >
            Fechar
          </button>
        </div>
      </div>
    </div>
  );

  return (
    <Layout>
      <div className="p-6 font-medium max-w-7xl mx-auto">
        <div className="flex justify-between items-center mb-8">
          <div className="flex items-center gap-2">
            <h2 className="text-3xl font-bold text-gray-800 flex items-center gap-2">Gestão de Natalidade <GiNewBorn size={38} /></h2>
            <AjudaTela {...AJUDA} />
          </div>
          {!mostrarFormulario && (
            <button
              onClick={handleNovoNascimento}
              className="bg-blue-600 hover:bg-blue-700 text-white px-6 py-2 rounded-lg transition-colors flex items-center gap-2 shadow-md"
            >
              <svg className="w-5 h-5" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M12 4v16m8-8H4" />
              </svg>
              Registrar Nascimento
            </button>
          )}
        </div>
        
        {/* Formulário de Cadastro/Edição */}
        {mostrarFormulario && (
          <div className="bg-white rounded-lg shadow-md p-6 mb-8 border border-gray-200">
            <div className="flex justify-between items-center mb-4">
              <h3 className="text-xl font-semibold text-gray-800">
                {editando ? "Editar Registro de Nascimento" : "Registrar Nascimento"}
              </h3>
              <button
                onClick={handleCancel}
                className="text-gray-500 hover:text-gray-700"
                title="Fechar formulário"
              >
                <svg className="w-6 h-6" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                  <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M6 18L18 6M6 6l12 12" />
                </svg>
              </button>
            </div>
            
            <form onSubmit={handleSubmit}>
              <div className="space-y-6">
                {/* Linha 1: Mãe, SISBOV, Brinco */}
                <div className="grid grid-cols-1 md:grid-cols-3 gap-4">
                  <div className="flex flex-col">
                    <label className="text-sm font-medium text-gray-700 mb-1 flex items-center gap-2">
                      Mãe *
                      <button
                        type="button"
                        onClick={() => setMostrarPopupMae(true)}
                        className="text-blue-500 hover:text-blue-700 transition-colors"
                        title="Informações sobre seleção de mãe"
                      >
                        <svg className="w-5 h-5" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                          <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M13 16h-1v-4h-1m1-4h.01M21 12a9 9 0 11-18 0 9 9 0 0118 0z" />
                        </svg>
                      </button>
                    </label>
                    <div className="flex items-center gap-2">
                      <select
                        name="mae"
                        value={formData.mae}
                        onChange={handleChange}
                        className="flex-1 border border-green-500 p-2 rounded focus:ring-2 focus:ring-green-300"
                        required
                      >
                        <option value="">Selecione a mãe...</option>
                        {Array.isArray(animais) && animais.map(a => (
                          <option key={a.id} value={a.id}>
                            {getAnimalDisplay(a)}
                          </option>
                        ))}
                      </select>
                      <button
                        type="button"
                        onClick={() => setMostrarModalAnimal(true)}
                        title="Cadastrar novo animal"
                        className="p-2 hover:bg-gray-200 rounded"
                      >
                        <LuCopyPlus size={26} className="text-green-600 hover:text-green-800" />
                      </button>
                    </div>
                    <span className="text-xs text-blue-600 mt-1">
                      Apenas fêmeas podem ser mães. Clique no ícone para ver detalhes por espécie.
                    </span>
                  </div>

                  <div className="flex flex-col">
                    <label className="text-sm font-medium text-gray-700 mb-1">SISBOV do Filhote</label>
                    <input
                      type="text"
                      name="sisbov"
                      value={formData.sisbov}
                      onChange={handleChange}
                      className="border border-green-500 p-2 rounded focus:ring-2 focus:ring-green-300"
                      placeholder="Número SISBOV"
                    />
                    <span className="text-xs text-gray-500 mt-1">Opcional - Identificação oficial</span>
                  </div>

                  <div className="flex flex-col">
                    <label className="text-sm font-medium text-gray-700 mb-1">Número do Brinco *</label>
                    <input
                      type="text"
                      name="brinco"
                      value={formData.brinco}
                      onChange={handleChange}
                      className="border border-green-500 p-2 rounded focus:ring-2 focus:ring-green-300"
                      placeholder="Número de identificação"
                      required
                    />
                  </div>
                </div>

                {/* Linha 2: Data Nascimento, Sexo, Peso */}
                <div className="grid grid-cols-1 md:grid-cols-3 gap-4">
                  <div className="flex flex-col">
                    <label className="text-sm font-medium text-gray-700 mb-1">Data de Nascimento *</label>
                    <input
                      type="date"
                      name="data_nascimento"
                      value={formData.data_nascimento}
                      onChange={handleChange}
                      className="border border-green-500 p-2 rounded focus:ring-2 focus:ring-green-300"
                      required
                    />
                  </div>

                  <div className="flex flex-col">
                    <label className="text-sm font-medium text-gray-700 mb-1">Sexo do Filhote *</label>
                    <select
                      name="sexo"
                      value={formData.sexo}
                      onChange={handleChange}
                      className="border border-green-500 p-2 rounded focus:ring-2 focus:ring-green-300"
                      required
                    >
                      <option value="">Selecione...</option>
                      <option value="M">Macho</option>
                      <option value="F">Fêmea</option>
                    </select>
                  </div>

                  <div className="flex flex-col">
                    <label className="text-sm font-medium text-gray-700 mb-1">Peso ao Nascer</label>
                    <div className="flex items-center border border-green-500 rounded">
                      <input
                        type="number"
                        step="0.01"
                        name="peso_nascimento"
                        value={formData.peso_nascimento}
                        onChange={handleChange}
                        className="w-full p-2 rounded-l focus:ring-2 focus:ring-green-300"
                        placeholder="0,00"
                      />
                      <span className="px-3 text-gray-700 bg-gray-100 rounded-r">kg</span>
                    </div>
                    <span className="text-xs text-gray-500 mt-1">Opcional - Peso ao nascer do filhote</span>
                  </div>
                </div>

                {/* Linha 3: Observações */}
                <div className="flex flex-col">
                  <label className="text-sm font-medium text-gray-700 mb-1">Observações</label>
                  <textarea
                    name="observacoes"
                    value={formData.observacoes}
                    onChange={handleChange}
                    rows={4}
                    className="border border-green-500 p-2 rounded focus:ring-2 focus:ring-green-300"
                    placeholder="Informações adicionais sobre o parto, condições do filhote, cuidados especiais, etc..."
                  />
                </div>

                {/* Botões do formulário */}
                <div className="flex gap-4 pt-4">
                  <button
                    type="submit"
                    disabled={loading}
                    className="bg-blue-600 hover:bg-blue-700 text-white px-6 py-2 rounded-lg transition-colors disabled:bg-blue-300"
                  >
                    {loading ? "Salvando..." : (editando ? "Atualizar" : "Registrar")}
                  </button>
                  <button
                    type="button"
                    onClick={handleCancel}
                    className="bg-red-600 hover:bg-red-700 text-white px-6 py-2 rounded-lg transition-colors"
                  >
                    Cancelar
                  </button>
                </div>
              </div>
            </form>
          </div>
        )}
        
        {/* Seção de Filtros - Sempre visível quando não está editando */}
        {!mostrarFormulario && (
          <div className="bg-white rounded-lg shadow-md p-6 mb-8 border border-gray-200">
            <h3 className="text-lg font-semibold text-gray-700 mb-4 flex items-center gap-2">
              <svg className="w-5 h-5" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M3 4a1 1 0 011-1h16a1 1 0 011 1v2.586a1 1 0 01-.293.707l-6.414 6.414a1 1 0 00-.293.707V17l-4 4v-6.586a1 1 0 00-.293-.707L3.293 7.293A1 1 0 013 6.586V4z" />
              </svg>
              Filtros de Busca
            </h3>
            
            <div className="grid grid-cols-1 md:grid-cols-4 gap-4">
              <div className="flex flex-col">
                <label className="text-sm font-medium text-gray-700 mb-1">Buscar por Brinco ou SISBOV</label>
                <input
                  type="text"
                  placeholder="Número do brinco ou SISBOV..."
                  value={filtros.busca}
                  onChange={(e) => setFiltros({...filtros, busca: e.target.value})}
                  className="border border-green-500 p-2 rounded focus:ring-2 focus:ring-green-300"
                />
              </div>
              
              <div className="flex flex-col">
                <label className="text-sm font-medium text-gray-700 mb-1">Sexo</label>
                <select
                  value={filtros.sexo}
                  onChange={(e) => setFiltros({...filtros, sexo: e.target.value})}
                  className="border border-green-500 p-2 rounded focus:ring-2 focus:ring-green-300"
                >
                  <option value="">Todos</option>
                  <option value="M">Macho</option>
                  <option value="F">Fêmea</option>
                </select>
              </div>
              
              <div className="flex flex-col">
                <label className="text-sm font-medium text-gray-700 mb-1">Data Início</label>
                <input
                  type="date"
                  value={filtros.data_inicio}
                  onChange={(e) => setFiltros({...filtros, data_inicio: e.target.value})}
                  className="border border-green-500 p-2 rounded focus:ring-2 focus:ring-green-300"
                />
              </div>
              
              <div className="flex flex-col">
                <label className="text-sm font-medium text-gray-700 mb-1">Data Fim</label>
                <input
                  type="date"
                  value={filtros.data_fim}
                  onChange={(e) => setFiltros({...filtros, data_fim: e.target.value})}
                  className="border border-green-500 p-2 rounded focus:ring-2 focus:ring-green-300"
                />
              </div>
            </div>
            
            <div className="flex gap-3 mt-4">
              <button
                onClick={aplicarFiltros}
                className="bg-green-600 hover:bg-green-700 text-white px-4 py-2 rounded transition-colors"
              >
                Aplicar Filtros
              </button>
              <button
                onClick={limparFiltros}
                className="bg-gray-500 hover:bg-gray-600 text-white px-4 py-2 rounded transition-colors"
              >
                Limpar Filtros
              </button>
            </div>
          </div>
        )}

        {/* Tabela de Nascimentos - Sempre visível quando não está editando */}
        {!mostrarFormulario && (
          <div className="bg-white rounded-lg shadow-md border border-gray-200 overflow-hidden">
            <div className="p-4 border-b border-gray-200 bg-gray-50">
              <p className="text-gray-600">
                {Array.isArray(nascimentosFiltrados) ? nascimentosFiltrados.length : 0} nascimento(s) registrado(s)
              </p>
            </div>
            
            {carregandoLista ? (
              <div className="p-8 text-center text-gray-500">Carregando...</div>
            ) : (
              <div className="overflow-x-auto">
                <table className="w-full">
                  <thead className="bg-gray-100">
                    <tr>
                      <th className="p-3 text-left text-sm font-semibold text-gray-700">Código</th>
                      <th className="p-3 text-left text-sm font-semibold text-gray-700">Data</th>
                      <th className="p-3 text-left text-sm font-semibold text-gray-700">Mãe</th>
                      <th className="p-3 text-left text-sm font-semibold text-gray-700">Brinco</th>
                      <th className="p-3 text-left text-sm font-semibold text-gray-700">SISBOV</th>
                      <th className="p-3 text-left text-sm font-semibold text-gray-700">Sexo</th>
                      <th className="p-3 text-left text-sm font-semibold text-gray-700">Peso</th>
                      <th className="p-3 text-left text-sm font-semibold text-gray-700">Observações</th>
                      <th className="p-3 text-left text-sm font-semibold text-gray-700">Ações</th>
                    </tr>
                  </thead>
                  <tbody>
                    {Array.isArray(nascimentosFiltrados) && nascimentosFiltrados.map((nascimento) => (
                      <tr key={nascimento.id} className="border-t border-gray-200 hover:bg-gray-50 transition-colors">
                        <td className="p-3 text-sm font-mono">{nascimento.id}</td>
                        <td className="p-3 text-sm">{formatarData(nascimento.data_nascimento)}</td>
                        <td className="p-3 text-sm">
                          <span className="flex items-center gap-1">
                            <svg className="w-4 h-4 text-pink-400" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                              <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M16 7a4 4 0 11-8 0 4 4 0 018 0zM12 14a7 7 0 00-7 7h14a7 7 0 00-7-7z" />
                            </svg>
                            {nascimento.mae_sisbov || nascimento.mae?.sisbov || `ID: ${nascimento.mae}`}
                          </span>
                        </td>
                        <td className="p-3 text-sm font-mono">{nascimento.brinco}</td>
                        <td className="p-3 text-sm font-mono">{nascimento.sisbov || "—"}</td>
                        <td className="p-3">
                          <span className={`px-2 py-1 rounded-full text-xs font-medium ${getSexoColor(nascimento.sexo)}`}>
                            {getSexoLabel(nascimento.sexo)}
                          </span>
                        </td>
                        <td className="p-3 text-sm">{formatarPeso(nascimento.peso_nascimento)}</td>
                        <td className="p-3 text-sm max-w-xs truncate">{nascimento.observacoes || "—"}</td>
                        <td className="p-3">
                          <div className="flex gap-2">
                            <button
                              onClick={() => handleEditar(nascimento)}
                              className="bg-yellow-500 hover:bg-yellow-600 text-white px-3 py-1 rounded text-sm transition-colors"
                              title="Editar"
                            >
                              Editar
                            </button>
                            <button
                              onClick={() => handleExcluir(nascimento.id)}
                              className="bg-red-600 hover:bg-red-700 text-white px-3 py-1 rounded text-sm transition-colors"
                              title="Excluir"
                            >
                              Excluir
                            </button>
                          </div>
                        </td>
                      </tr>
                    ))}
                    
                    {(!Array.isArray(nascimentosFiltrados) || nascimentosFiltrados.length === 0) && (
                      <tr>
                        <td colSpan="9" className="p-8 text-center text-gray-500">
                          {filtros.busca || filtros.sexo || filtros.data_inicio || filtros.data_fim
                            ? "Nenhum nascimento encontrado com os filtros selecionados."
                            : "Nenhum nascimento registrado."}
                        </td>
                      </tr>
                    )}
                  </tbody>
                </table>
              </div>
            )}
          </div>
        )}
      </div>
      
      {mostrarPopupMae && <PopupInfoMae />}

      <ModalCadastroAnimal
        isOpen={mostrarModalAnimal}
        onClose={() => setMostrarModalAnimal(false)}
        onSuccess={async (novo) => {
          await carregarAnimais();
          setFormData((prev) => ({ ...prev, mae: novo.id }));
        }}
      />
    </Layout>
  );
}